import { useEffect, useRef, useState, useCallback } from "react";
import { WS_BASE } from "@/lib/api";

/**
 * useGameSocket — thin WebSocket wrapper for /api/game-ws/{roomId}.
 *
 * Emits every incoming message to `onMessage` and exposes a `send(obj)` helper.
 * Automatically closes on unmount and reconnects once on transient errors.
 *
 * Returns:
 *   { connected, hello, send, close, error }
 *   - `hello` is the first "hello" payload the server sends (carries side + peers)
 */
export default function useGameSocket({ roomId, enabled = true, onMessage }) {
  const wsRef = useRef(null);
  const onMsgRef = useRef(onMessage);
  const reconnectAttemptRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const [hello, setHello] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { onMsgRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!enabled || !roomId) return;
    const token = localStorage.getItem("sc_token");
    if (!token) { setError("not_authenticated"); return; }

    let cancelled = false;
    const connect = () => {
      const url = `${WS_BASE}/api/game-ws/${encodeURIComponent(roomId)}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => { if (!cancelled) { setConnected(true); setError(null); reconnectAttemptRef.current = 0; } };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "hello") setHello(m);
          onMsgRef.current && onMsgRef.current(m);
        } catch { /* ignore malformed */ }
      };
      ws.onclose = (ev) => {
        if (cancelled) return;
        setConnected(false);
        // Auth / not-found / room-full → don't retry
        if ([4401, 4403, 4404].includes(ev.code)) { setError(`close_${ev.code}`); return; }
        // Single retry after 1s
        if (reconnectAttemptRef.current < 1) {
          reconnectAttemptRef.current += 1;
          setTimeout(() => { if (!cancelled) connect(); }, 1000);
        }
      };
      ws.onerror = () => { /* handled by onclose */ };
    };
    connect();
    return () => {
      cancelled = true;
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
    };
  }, [roomId, enabled]);

  const send = useCallback((obj) => {
    const w = wsRef.current;
    if (!w || w.readyState !== 1) return false;
    try { w.send(JSON.stringify(obj)); return true; } catch { return false; }
  }, []);

  const close = useCallback(() => {
    try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
  }, []);

  return { connected, hello, send, close, error };
}
